import type {
  CellData,
  GridId,
  GridMap,
  GridPosition,
  ScreenLayout,
  ScreenMethods,
} from "@observe-tech/utils";
import type { SyncStore as Store } from "../../infra/state";
import { findNextCellIndex, getCellsWithData } from "./grid-utils";

const updateMachinesLoadedCameraOrder = (
  store: Store,
  machineId: number,
  cageId: number,
  cameras: number[],
) => {
  const machinesLoaded = store.get("machinesLoaded") || {};
  const machine = machinesLoaded[machineId];
  if (!machine) return;
  const cage = machine.find(
    (c: { cageId: number; cameras: number[] }) => c.cageId === cageId,
  );
  if (!cage) return;
  cage.cameras = cameras;
  store.set("machinesLoaded", machinesLoaded);
};

export const createCameraOperations = (
  store: Store,
  findScreenLayout: (screenId: number) => ScreenLayout,
  setScreenLayout: (screenId: number, layout: ScreenLayout) => ScreenLayout,
  shouldUseCameraGrid: (layout: ScreenLayout) => boolean,
  gridToUse: (layout: ScreenLayout) => GridMap,
  positionToGridId: (position: GridPosition) => GridId,
  findExistingCameraPosition: (
    store: Store,
    targetData: CellData,
    excludeGridId?: GridId,
  ) => { screenId: number; gridId: GridId; data: CellData } | null,
) => {
  const swapCamerasInPosition: ScreenMethods["swapCamerasInPosition"] = (
    id,
    position,
    sourceCam,
    targetCam,
  ) => {
    const layout = findScreenLayout(id);

    const gridId = positionToGridId(position);
    const gridToUseForLayout = gridToUse(layout);
    const currentData = gridToUseForLayout[gridId];

    const isUsingCameraGrid = shouldUseCameraGrid(layout);

    if (isUsingCameraGrid) {
      throw new Error(
        "Cannot change camera position in a single camera layout",
      );
    }

    if (!currentData) {
      throw new Error(`No cage data found at position: ${gridId}`);
    }

    // Work with current grid cameras, not original machine data
    const currentCameras = [...currentData.cameras];

    // Validate source and target cameras against current grid state
    if (
      !currentCameras.includes(sourceCam) ||
      !currentCameras.includes(targetCam)
    ) {
      throw new Error(
        `Source or target camera not found in current cage: ${currentData.cageId} for machine: ${currentData.machineId}`,
      );
    }

    const sourceIndex = currentCameras.indexOf(sourceCam);
    const targetIndex = currentCameras.indexOf(targetCam);
    [currentCameras[sourceIndex], currentCameras[targetIndex]] = [
      currentCameras[targetIndex],
      currentCameras[sourceIndex],
    ];

    gridToUseForLayout[gridId] = {
      ...currentData,
      cameras: currentCameras as [number, ...number[]],
    };
    setScreenLayout(id, layout);

    updateMachinesLoadedCameraOrder(
      store,
      currentData.machineId,
      currentData.cageId,
      currentCameras,
    );
  };

  const cycleToPenInDirection = (
    id: number,
    direction: "next" | "previous",
  ): { success: boolean; newPosition?: GridPosition } => {
    const layout = findScreenLayout(id);
    const { fullScreen, gridLayout } = layout.screenData;

    if (!fullScreen) {
      return { success: false };
    }

    const gridToUseForLayout = gridToUse(layout);
    const cellsWithPens = getCellsWithData(gridToUseForLayout, gridLayout);

    if (cellsWithPens.length === 0) {
      return { success: false };
    }

    const newIndex = findNextCellIndex(
      cellsWithPens,
      fullScreen,
      direction,
      gridLayout.columns,
    );

    if (newIndex === -1) {
      return { success: false };
    }

    const newPosition = cellsWithPens[newIndex].position;
    const newGridId = cellsWithPens[newIndex].gridId;
    const newCellData = gridToUseForLayout[newGridId];

    layout.screenData.fullScreen = newGridId;
    setScreenLayout(id, layout);

    if (newCellData?.cameras?.[0]) {
      store.set("selected", {
        screenId: id,
        cameraId: newCellData.cameras[0],
        machineId: newCellData.machineId,
      });
    }

    return { success: true, newPosition };
  };

  const cycleCamerasInPosition: ScreenMethods["cycleCamerasInPosition"] = (
    id,
    position,
  ) => {
    const layout = findScreenLayout(id);

    const gridId = positionToGridId(position);
    const gridToUseForLayout = gridToUse(layout);
    const currentData = gridToUseForLayout[gridId];

    if (!currentData) {
      throw new Error(`No data found at position: ${gridId}`);
    }

    const isUsingCameraGrid = shouldUseCameraGrid(layout);
    let nextCameraData: typeof currentData;

    if (isUsingCameraGrid) {
      // Handle single camera layout using cameraGrid
      const machinesLoaded = store.get("machinesLoaded");
      const machineData = machinesLoaded[currentData.machineId];
      if (!machineData) {
        throw new Error(
          `No machine data found for machineId: ${currentData.machineId}`,
        );
      }

      const cageData = machineData.find(
        (cage: { cageId: number; cameras: number[] }) =>
          cage.cageId === currentData.cageId,
      );
      if (!cageData) {
        throw new Error(
          `No cage data found for cageId: ${currentData.cageId} in machineId: ${currentData.machineId}`,
        );
      }

      const availableCameras = cageData.cameras;
      if (availableCameras.length <= 1) {
        // No need to cycle if there's only one camera
        return;
      }

      // Find current camera index and cycle to next
      const currentCameraIndex = availableCameras.indexOf(
        currentData.cameras[0],
      );
      const nextCameraIndex =
        (currentCameraIndex + 1) % availableCameras.length;
      const nextCamera = availableCameras[nextCameraIndex];

      nextCameraData = {
        ...currentData,
        cameras: [nextCamera],
      };
    } else {
      // Handle multi-camera layout using cageGrid
      const currentCameras = [...currentData.cameras];

      if (currentCameras.length <= 1) {
        // No need to cycle if there's only one camera
        return;
      }

      // Shift array by one: move first element to the end
      const [firstCamera, ...restCameras] = currentCameras;
      const cycledCameras = [...restCameras, firstCamera];

      nextCameraData = {
        ...currentData,
        cameras: cycledCameras as [number, ...number[]],
      };
    }

    // Check for duplicates across all screens before applying the change
    const existingPosition = findExistingCameraPosition(
      store,
      nextCameraData,
      gridId,
    );

    const currentOriginalPin = currentData?.pin;

    if (existingPosition) {
      // Found a duplicate, swap the data instead of creating conflict
      if (existingPosition.screenId === id) {
        // Same screen swap
        const existingOriginalPin =
          gridToUseForLayout[existingPosition.gridId]?.pin;
        gridToUseForLayout[existingPosition.gridId] = currentData;
        const sameScreenCell = gridToUseForLayout[existingPosition.gridId];
        if (sameScreenCell) {
          sameScreenCell.pin = existingOriginalPin;
        }
      } else {
        // Cross-screen swap
        const existingLayout = findScreenLayout(existingPosition.screenId);
        const existingGrid = gridToUse(existingLayout);
        const existingOriginalPin = existingGrid[existingPosition.gridId]?.pin;
        existingGrid[existingPosition.gridId] = currentData;
        const crossScreenCell = existingGrid[existingPosition.gridId];
        if (crossScreenCell) {
          crossScreenCell.pin = existingOriginalPin;
        }
        setScreenLayout(existingPosition.screenId, existingLayout);
      }
    }

    gridToUseForLayout[gridId] = nextCameraData;
    const targetCell = gridToUseForLayout[gridId];
    if (targetCell) {
      targetCell.pin = currentOriginalPin;
    }
    setScreenLayout(id, layout);

    if (!isUsingCameraGrid) {
      updateMachinesLoadedCameraOrder(
        store,
        nextCameraData.machineId,
        nextCameraData.cageId,
        [...nextCameraData.cameras],
      );
    }

    if (nextCameraData?.cameras?.[0]) {
      const current = store.get("selected");
      if (current && current.screenId === id) {
        store.set("selected", {
          screenId: id,
          cameraId: nextCameraData.cameras[0],
          machineId: nextCameraData.machineId,
        });
      }
    }
  };

  const cycleToNextPen = (id: number) => cycleToPenInDirection(id, "next");
  const cycleToPreviousPen = (id: number) =>
    cycleToPenInDirection(id, "previous");

  return {
    swapCamerasInPosition,
    cycleCamerasInPosition,
    cycleToNextPen,
    cycleToPreviousPen,
  };
};
